import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, X, Send, Play, Check, AlertCircle, Clock, Paperclip, Image as ImageIcon, Calendar } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { AuthImageLightbox } from "@/components/AuthImageLightbox";

const STATUS_STYLES = {
  OPEN: "bg-sky-100 text-sky-700 border-sky-200",
  IN_PROGRESS: "bg-amber-100 text-amber-700 border-amber-200",
  COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  OVERDUE: "bg-red-100 text-red-700 border-red-200",
};

const PRIORITY_STYLES = {
  HIGH: "bg-red-100 text-red-700 border-red-200",
  MEDIUM: "bg-amber-100 text-amber-700 border-amber-200",
  LOW: "bg-slate-100 text-slate-700 border-slate-200",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ts; }
}

function buildTimeline(task) {
  const entries = [];
  entries.push({ kind: "CREATED", label: "Task created", at: task.created_at, by: task.created_by_name || task.created_by || "Admin" });
  if (task.started_at) entries.push({ kind: "STARTED", label: "Started", at: task.started_at, by: task.assigned_to_name });
  if (task.completed_at) entries.push({ kind: "COMPLETED", label: "Completed", at: task.completed_at, by: task.completed_by_name || task.assigned_to_name });
  if (task.status === "OVERDUE") entries.push({ kind: "OVERDUE", label: "Marked overdue", at: task.updated_at, by: "System" });
  return entries;
}

const TIMELINE_ICONS = {
  CREATED:   { Icon: Send,         ring: "border-sky-400 bg-sky-50 text-sky-600" },
  STARTED:   { Icon: Play,         ring: "border-amber-400 bg-amber-50 text-amber-600" },
  COMPLETED: { Icon: Check,        ring: "border-emerald-400 bg-emerald-50 text-emerald-600" },
  OVERDUE:   { Icon: AlertCircle,  ring: "border-red-400 bg-red-50 text-red-600" },
};

/**
 * <TaskDetailDialog> — read-only view of a task with attachment preview and
 * an approval timeline (Created → Started → Completed/Overdue).
 */
export function TaskDetailDialog({ open, onClose, task, employee, creator }) {
  const [lightboxPath, setLightboxPath] = useState(null);
  if (!task) return null;
  const attachments = task.attachments || task.photos || [];
  const timeline = buildTimeline(task);
  const dueDate = task.due_date ? new Date(task.due_date) : null;
  const daysToDue = dueDate ? Math.ceil((dueDate.getTime() - Date.now()) / 86400000) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#8B1A1A]" />
              <span className="truncate">{task.title}</span>
              <Badge variant="outline" className={`ml-1 text-xs ${STATUS_STYLES[task.status] || ""}`}>{task.status}</Badge>
              {task.priority && (
                <Badge variant="outline" className={`text-xs ${PRIORITY_STYLES[task.priority] || ""}`}>{task.priority}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Assigned to</p>
                <p className="text-sm font-medium mt-0.5">{employee?.full_name || task.assigned_to_name || task.assigned_to || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Type</p>
                <p className="text-sm font-medium mt-0.5">{(task.task_type || "—").replace(/_/g, " ")}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Due date</p>
                <p className="text-sm font-medium mt-0.5 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {task.due_date || "—"}
                  {daysToDue !== null && task.status !== "COMPLETED" && (
                    <span className={`text-[10px] ml-1 ${daysToDue < 0 ? "text-red-600" : daysToDue <= 2 ? "text-amber-600" : "text-slate-400"}`}>
                      ({daysToDue < 0 ? `${Math.abs(daysToDue)}d overdue` : daysToDue === 0 ? "today" : `${daysToDue}d left`})
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Description */}
            {task.description && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Description</p>
                <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{task.description}</p>
              </div>
            )}

            {/* Attachments */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Attachments <span className="text-slate-400 font-normal">({attachments.length})</span>
                </p>
              </div>
              {attachments.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No attachments.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="task-attachment-grid">
                  {attachments.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxPath(p)}
                      className="aspect-square rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-[#8B1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]"
                      data-testid={`task-attachment-${i}`}
                    >
                      <AuthImage path={p} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Task timeline</p>
              </div>
              <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-3" data-testid="task-timeline">
                {timeline.map((t, i) => {
                  const meta = TIMELINE_ICONS[t.kind] || TIMELINE_ICONS.CREATED;
                  const Icon = meta.Icon;
                  return (
                    <li key={i} className="ml-4" data-testid={`task-timeline-${t.kind.toLowerCase()}`}>
                      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full border-2 ${meta.ring}`}>
                        <Icon className="w-3 h-3" />
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{t.label}</p>
                        <p className="text-[11px] text-slate-500">{fmt(t.at)}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        by <span className="font-medium text-slate-700 dark:text-slate-300">{t.by || "—"}</span>
                      </p>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightboxPath && (
        <AuthImageLightbox path={lightboxPath} onClose={() => setLightboxPath(null)} />
      )}
    </>
  );
}

export default TaskDetailDialog;
